import moment from 'moment';
import ls from 'local-storage';
import i18n from '../i18n';
import api from '../api';

const _ = require('lodash');

const SYMPTOM_SELECTION_PHASE_ORDINAL_NUMBER = 1;

const METHOD_PATH = 0;
const METHOD_CODE = 1;

class SelectionHandler {

  constructor(config, namespace) {
    // console.log('new SelectionHandler created!');
    const storedSelectionHandler = ls.get('selectionHandler');
    if (storedSelectionHandler) {
      const parsedSelectionHandler = JSON.parse(storedSelectionHandler);
      const { config: configStored, namespace: namespaceStored, selections, currentMethod, code, byTicket, ticket } = parsedSelectionHandler;
      this.config = configStored;
      this.namespace = namespaceStored;
      this.selections = selections;
      this.currentMethod = currentMethod;
      this.code = code;
      this.byTicket = byTicket;
      this.ticket = ticket;
      return this;
    }
    this.config = config;
    this.namespace = namespace;
    this.selections = [];
    this.currentMethod = METHOD_PATH;
    this.code = undefined;
    this.byTicket = false;
    this.ticket = undefined;
    this.dedicatedResources = null;
  }

  checkOptionId(optionId) {
    // Valid id's are in form of '1', '1.2', '2.1.3' etc.
    return /^\d+(\.\d+)*$/.test(optionId);
  }

  useCode() {
    this.currentMethod = METHOD_CODE;
    this.selections = [];
  }

  setCode(code) {
    this.useCode();
    this.code = code;
  }

  setIsByTicket() {
    this.byTicket = true;
  }

  setTicket(ticket) {
    this.ticket = ticket;
    this.dedicatedResources = null;
  }

  removeByTicket() {
    this.byTicket = false;
    this.ticket = undefined;
  }

  isByTicket() {
    return this.byTicket && this.ticket;
  }

  hasCode() {
    return (this.code !== undefined);
  }

  getCode() {
    return this.code;
  }

  usePath() {
    this.currentMethod = METHOD_PATH;
    this.code = undefined;
  }

  isUsingPath() {
    return (this.currentMethod === METHOD_PATH);
  }

  isUsingCode() {
    return (this.currentMethod === METHOD_CODE);
  }

  select(optionId) {
    let wasOptionSelected = false;
    if (this.checkOptionId(optionId)) {
      const level = optionId.split('.').length;
      const index = level - 1;
      if (this.getSelectedOptionIdOnLevel(level) !== optionId) {
        // TODO: Check that given optionId is among options of previous levels.
        this.selections[index] = this.getOptionData(optionId);
        this.selections.splice(index + 1);
        wasOptionSelected = true;
      }
    }
    return wasOptionSelected;
  }

  setSelectedOptionIds(optionIds) {
    let success = false;
    if (_.isArray(optionIds)) {
      if (optionIds.length === 0) {
        this.selections = [];
        success = true;
      } else {
        success = _.every(optionIds, optionId => this.select(optionId));
      }
    }
    return success;
  }

  getSelectedOptions() {
    return this.isUsingPath() ? this.selections : [{ title: 'Koodi' }, { title: this.getCode() }];
  }

  getSelectedOptionIds() {
    return this.selections.map(option => option.id);
  }

  isFinalLevelReached() {
    const levels = this.selections.length;
    if (levels > 0) {
      const optionsForNextLevel = this.getOptionsForLevel(levels + 1);
      return (optionsForNextLevel.length === 0);
    } else if (this.isByTicket() && this.ticket) {
      return true;
    }
    return false;
  }

  getTitleForPath() {
    return `${SYMPTOM_SELECTION_PHASE_ORDINAL_NUMBER}. ${i18n.t(`${this.namespace}:pathTitle`, { defaultValue: '' })}`;
  }

  getLocalizationForLevel(level, key) {
    let translationKey;
    if (level === 1) {
      translationKey = `${this.namespace}:pathConfig.${key}`;
    } else if (this.isLevelSelected(level - 1)) {
      const option = this.getSelectedOptionOnLevel(level - 1);
      translationKey = `${this.namespace}:pathConfig.${option.id}.${key}`;
    }
    return i18n.t(translationKey, { defaultValue: '' });
  }

  getPromptForLevel(level) {
    return this.getLocalizationForLevel(level, 'prompt');
  }

  getPromptTextForLevel(level) {
    return this.getLocalizationForLevel(level, 'promptText');
  }

  getInfoForLevel(level) {
    return this.getLocalizationForLevel(level, 'info');
  }

  getOptionsForLevel(level) {
    if (level === 1) {
      return this.config.options;
    } else if (this.isLevelSelected(level - 1)) {
      const previousOption = this.getSelectedOptionOnLevel(level - 1);
      return previousOption.options || [];
    }
    return [];
  }

  isLevelSelected(level) {
    return (typeof this.selections[level - 1] !== 'undefined');
  }

  getLastSelectedOption() {
    const lastLevel = this.selections.length;
    return this.getSelectedOptionOnLevel(lastLevel);
  }

  getSelectedOptionOnLevel(level) {
    if (this.isByTicket() && this.ticket) {
      const { unit, group, speciality, timeSlotDefinitions } = this.ticket;

      if (this.ticket.isMovendosTimeReservationTicket) {    
        const { specialities, inGroups, notInGroups } = timeSlotDefinitions[0];
        const { resources, inUnits } = timeSlotDefinitions[0];

        const ticketDataAsPathResource = {
          type: 1,
          timeType: [this.ticket.timeSlotDefinitions[0].timeTypeDefinitions[0].timeType],
          ...(inUnits.length && { inUnits }),
          ...(inGroups.length && { inGroups }),
          ...(notInGroups.length && { notInGroups }),
          ...(specialities.length && { speciality: specialities }),
          ...(resources.length && { inResources: resources })
        };
        return {
          id: this.ticket.code,
          reservation: {
            resources: [ticketDataAsPathResource],
            duration: this.ticket.timeSlotDefinitions[0].duration,
            fromTicketOpeningDate: moment(this.ticket.openingDate, 'YYYY-MM-DD'),
            toTicketClosingDate: moment(this.ticket.closingDate, 'YYYY-MM-DD')
          }
        };
      } 
      const { resources } = this.ticket;
      const ticketDataAsPathResource = {
        type: 1,
        timeType: [this.ticket.timeType],
        ...(unit && { inUnits: [unit] }),
        ...(group && { inGroups: [group] }),
        ...(speciality && { speciality }),
        ...(resources && { inResources: resources })
      };
      return {
        id: this.ticket.code,
        reservation: {
          resources: [ticketDataAsPathResource],
          duration: this.ticket.duration,
          fromTicketOpeningDate: moment(this.ticket.openingDate, 'YYYY-MM-DD'),
          toTicketClosingDate: moment(this.ticket.closingDate, 'YYYY-MM-DD')
        }
      };
    }
    return this.selections[level - 1] || false;
  }

  getSelectedOptionIdOnLevel(level) {
    return this.isLevelSelected(level) ? this.getSelectedOptionOnLevel(level).id : false;
  }

  getOptionData(optionId) {
    let optionData;
    if (this.checkOptionId(optionId)) {
      optionData = this.config;
      const optionIdParts = optionId.split('.');
      do {
        const options = optionData.options || [];
        const currentSelectionNumber = optionIdParts.shift();
        optionData = options.filter(option => (option.id.split('.').pop() === currentSelectionNumber))[0];
      } while (optionIdParts.length > 0);
    }
    return optionData;
  }
}

export default SelectionHandler;



// WEBPACK FOOTER //
// ./src/util/SelectionHandler.js